import {
  createQColumnHelper,
  DataProvider,
  QCard,
  QCollapse,
  QDataTable,
  QHeading,
  QIconButton,
  QStack,
} from "@qualio/ui-components";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ReviewHistory, ReviewStatus } from "@design-controls/types";
import { sortByCode } from "../../sortByCode";

type Props = {
  changeHistory: ReviewHistory[];
};

const columnHelper = createQColumnHelper<ReviewHistory>();

export const DesignReviewOverviewTable: React.FC<Props> = ({
  changeHistory,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const statusLabels: Record<ReviewStatus, string> = {
    IN_PROGRESS: t("statuses.review.draft"),
    SENT_FOR_APPROVAL: t("statuses.review.forApproval"),
    APPROVED: t("statuses.review.approved"),
    NOT_APPROVED: t("statuses.review.declined"),
  };

  const columns = [
    columnHelper.code("code", {
      header: t("reviewHistory.table.header.code"),
      minWidth: "100px",
      width: "10%",
    }),
    {
      accessorKey: "title",
      header: t("reviewHistory.table.header.title"),
      meta: {
        minWidth: "300px",
        width: "60%",
      },
      cell: ({
        row: { original: row },
      }: {
        row: { original: ReviewHistory };
      }) => {
        return (
          <Link to={`../change-control/${row.id}/view`} target="_blank">
            {row.title}
          </Link>
        );
      },
    },
    columnHelper.date((record) => new Date(record.date), {
      header: t("reviewHistory.table.header.date"),
      minWidth: "150px",
      width: "10%",
    }),
    columnHelper.status(
      (record) => (record.status ? statusLabels[record.status] : ""),
      {
        header: t("reviewHistory.table.header.status"),
        statuses: {
          [t("statuses.review.draft")]: "gray",
          [t("statuses.review.forApproval")]: "blue",
          [t("statuses.review.approved")]: "green",
          [t("statuses.review.declined")]: "red",
        },
        minWidth: "150px",
        width: "10%",
      },
    ),
  ];

  return (
    <QCard data-cy="design-review-history">
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          {t("reviewHistory.heading")}
        </QHeading>
        <QIconButton
          onClick={() => setExpanded((s) => !s)}
          iconName={expanded ? "ChevronUp" : "ChevronDown"}
          aria-label={
            expanded
              ? t("reviewHistory.collapseExpand.collapse")
              : t("reviewHistory.collapseExpand.expand")
          }
        />
      </QStack>
      <QCollapse isOpen={expanded}>
        <DataProvider.Fixed data={changeHistory.sort(sortByCode).reverse()}>
          <QDataTable columns={columns} hideItemCount enableSorting={false} />
        </DataProvider.Fixed>
      </QCollapse>
    </QCard>
  );
};
